import React from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { OpenInNew, CheckCircle, Cancel } from '@mui/icons-material';
import { useGetPrChecksQuery } from '../../store/api/githubApi';
import { SideDrawer } from '../common/SideDrawer';

export const PrChecksDialog: React.FC<{ repoId: number; open: boolean; onClose: () => void }> = ({
  repoId,
  open,
  onClose,
}) => {
  const { data: checks, isLoading } = useGetPrChecksQuery(repoId, { skip: !open });

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="PR Checks"
      description="Recent pull request gate checks and automated evaluations"
      width={{ xs: '100vw', sm: 500, md: 540 }}
      footer={
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            fontWeight: 800,
            textTransform: 'none',
            px: 3,
            borderRadius: '10px',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
          }}
        >
          Close
        </Button>
      }
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !checks || checks.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No PRs checked yet. Enable the PR gate and open a pull request to see results here.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {checks.map((c) => (
            <ListItem
              key={c.id}
              divider
              sx={{
                py: 2,
                px: 1,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
              secondaryAction={
                c.checked_pr_url ? (
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNew fontSize="small" />}
                    href={c.checked_pr_url}
                    target="_blank"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                    }}
                  >
                    View PR
                  </Button>
                ) : undefined
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {c.status === 'failed' ? (
                      <Chip label="Error" size="small" />
                    ) : c.pr_gate_passed === false ? (
                      <Chip icon={<Cancel sx={{ fontSize: '14px !important' }} />} label="Failed" size="small" color="error" />
                    ) : c.pr_gate_passed === true ? (
                      <Chip icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} label="Passed" size="small" color="success" />
                    ) : (
                      <Chip label="Running" size="small" />
                    )}
                    <Typography variant="body2" fontWeight={800} color="#0f172a">
                      PR #{c.checked_pr_number}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                    {[
                      c.count_critical ? `${c.count_critical} critical` : null,
                      c.count_high ? `${c.count_high} high` : null,
                      c.count_medium ? `${c.count_medium} medium` : null,
                      c.count_low ? `${c.count_low} low` : null,
                    ].filter(Boolean).join(' · ') || (c.status === 'failed' ? c.status_message ?? 'Scan failed' : 'No new issues')}
                    {' · '}{new Date(c.created_at).toLocaleString()}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </SideDrawer>
  );
};

export default PrChecksDialog;
